<template>
    <section class="bo-card">
        <FormTabHeader
            icon="fal fa-poll-h"
            :title="$t('backoffice_infos.sections.fields')"
        />
        <div class="bo-body">
            <div
                v-for="form in forms"
                :key="'fields-' + form.id"
                class="bo-form-block"
                :class="{ 'bo-form-block--open': openedId === form.id }"
            >
                <div class="bo-form-block-header" @click="toggle(form.id)">
                    <div class="bo-form-block-title">
                        <i class="fal fa-chevron-right bo-chevron"></i>
                        <i class="fal fa-file-alt bo-form-icon"></i>
                        <span class="bo-form-name">{{ form.name }}</span>
                        <span class="bo-form-meta">
                            {{ form.counts ? form.counts.attributes : 0 }} {{ $t('backoffice_infos.fields_count') }}
                        </span>
                    </div>
                    <router-link
                        :to="`/forms/${form.id}`"
                        class="bo-action-btn"
                        @click.native.stop
                    >
                        <i class="fal fa-eye"></i>
                        {{ $t('form_types.view') }}
                    </router-link>
                </div>
                <BmvCollapse :open="openedId === form.id">
                    <div class="bo-form-block-body">
                        <div v-if="form.fields && form.fields.length" class="bof-steps">
                            <div
                                v-for="step in form.fields"
                                :key="step.id"
                                class="bof-step"
                            >
                                <div class="bof-step-header">
                                    <span class="bof-step-name">{{ step.name }}</span>
                                    <span class="bof-step-count">
                                        {{ step.attributes ? step.attributes.length : 0 }}
                                    </span>
                                </div>
                                <ul class="bof-attrs">
                                    <li
                                        v-for="attr in step.attributes"
                                        :key="attr.id"
                                        class="bof-attr"
                                    >
                                        <span class="bof-attr-dot"></span>
                                        {{ attr.label }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <p v-else class="bo-empty">{{ $t('backoffice_infos.no_fields') }}</p>
                    </div>
                </BmvCollapse>
            </div>
        </div>
    </section>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import FormTabHeader from '@/components/Model/Form/FormComponents/FormTabHeader'
import BmvCollapse from '@/components/UI/v2/BmvCollapse.vue'

export default {
    name: 'BackofficeFields',
    components: { FormTabHeader, BmvCollapse },
    props: {
        forms: { type: Array, required: true }
    },
    data() {
        return {
            openedId: null
        }
    },
    methods: {
        toggle(formId) {
            this.openedId = this.openedId === formId ? null : formId
        }
    }
}
</script>

<style scoped>
.bo-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.bo-body {
    padding: var(--v2-space-4) var(--v2-space-5);
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-3);
}

.bo-form-block {
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    overflow: hidden;
}

.bo-form-block-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    padding: 12px 16px;
    background: var(--v2-surface-1);
    cursor: pointer;
    transition: background var(--v2-transition-fast);
}

.bo-form-block-header:hover {
    background: var(--v2-surface-2);
}

.bo-form-block-title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex-wrap: wrap;
}

.bo-chevron {
    font-size: 0.75rem;
    color: var(--v2-text-3);
    width: 10px;
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.bo-form-block--open .bo-chevron {
    transform: rotate(90deg);
    color: var(--v2-primary);
}

.bo-form-icon {
    font-size: 0.85rem;
    color: var(--v2-primary);
}

.bo-form-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.bo-form-meta {
    font-size: 0.72rem;
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-medium);
    font-variant-numeric: tabular-nums;
}

.bo-form-block-body {
    padding: var(--v2-space-4);
    background: var(--v2-surface-0);
    border-top: 1px solid var(--v2-border-subtle);
}

.bof-steps {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: var(--v2-space-3);
}

.bof-step {
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    overflow: hidden;
    background: var(--v2-surface-0);
}

.bof-step-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 14px;
    background: var(--v2-surface-1);
    border-bottom: 1px solid var(--v2-border);
}

.bof-step-name {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-primary);
    letter-spacing: -0.005em;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bof-step-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 7px;
    border-radius: 999px;
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 0.66rem;
    font-weight: var(--v2-font-weight-bold);
    font-variant-numeric: tabular-nums;
}

.bof-attrs {
    list-style: none;
    margin: 0;
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.bof-attr {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-2);
}

.bof-attr-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--v2-border-strong);
    flex-shrink: 0;
}

.bo-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 10px;
    border-radius: var(--v2-radius-sm);
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    color: var(--v2-text-2);
    text-decoration: none !important;
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    flex-shrink: 0;
}

.bo-action-btn i {
    font-size: 0.72rem;
}

.bo-action-btn:hover {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.bo-empty {
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-4);
    font-style: italic;
}

@media (max-width: 768px) {
    .bo-body {
        padding: var(--v2-space-3) var(--v2-space-4);
    }

    .bo-form-block-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
}
</style>
